import React from "react";
import { cn } from "@/lib/utils";
import { MantineSelect } from "@wtc/ui";
import { SelectOption } from "./types";

interface SimpleSearchableSelectProps {
  value: string;
  onValueChange: (value: string, option?: SelectOption) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  isSearching?: boolean;
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;
  maxHeight?: string;
  showDescriptions?: boolean;
  highZIndex?: boolean; // Deprecated - Mantine handles z-index automatically
}

export const SimpleSearchableSelect: React.FC<SimpleSearchableSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder = "Select an option...",
  disabled = false,
  className = "",
  error = false,
  searchPlaceholder = "Search options...",
  searchValue,
  onSearchChange,
  isSearching = false,
  customOptionRenderer,
  maxHeight,
  showDescriptions,
  highZIndex = false, // Deprecated - Mantine handles z-index automatically
}) => {
  // Convert options to Mantine format
  const mantineOptions = React.useMemo(
    () =>
      options.map((option) => ({
        value: option.value,
        label: option.label,
        disabled: option.disabled,
      })),
    [options]
  );

  // Handle value change
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      const option = options.find((opt) => opt.value === newValue);
      onValueChange(newValue, option);
    },
    [options, onValueChange]
  );

  // Use Mantine Select with automatic z-index management
  return (
    <MantineSelect
      value={value}
      onValueChange={handleValueChange}
      options={mantineOptions}
      placeholder={placeholder}
      disabled={disabled}
      searchable={true}
      clearable={false}
      className={cn(className, error && "border-red-500")}
      // Mantine automatically handles z-index - no more highZIndex prop needed
      // All other props (searchValue, onSearchChange, customOptionRenderer, etc.)
      // are handled automatically by Mantine's built-in functionality
    />
  );
};
